import { useTranslation } from "react-i18next";

import type { MyRobotDetail } from "@/entities/user-robot";
import { formatMoney, formatPercent, moneySign } from "@/shared/lib";
import { Panel } from "@/shared/ui";

/**
 * «Мой результат» цифрами (ТЗ §17, §21): что вложено, что заработано, сколько
 * робот работает.
 *
 * Внесённые средства и торговый результат стоят РАЗНЫМИ строками — это
 * требование §22. Пополнение увеличивает капитал и не трогает `earned_total`,
 * поэтому смешать их в одной цифре «сколько у меня в роботе» значило бы
 * выдать взнос за доход.
 *
 * Строка «ожидает подключения» появляется только когда есть чему ожидать:
 * `capital_pending` считает бэкенд по журналу взносов, клиент ничего не
 * вычитает (правило ТЗ №5).
 */
export function MyResultSummary({ robot }: { robot: MyRobotDetail }) {
  const { t } = useTranslation();

  const sign = moneySign(robot.earned_total);
  const tone =
    sign < 0 ? "text-status-loss" : sign > 0 ? "text-status-profit" : "text-text-secondary";
  const pending = moneySign(robot.capital_pending) > 0;

  const rows: { label: string; value: string; tone?: string }[] = [
    {
      label: t("myRobots.capital.connected"),
      value: formatMoney(robot.capital_connected, { suffix: "USDT" }),
    },
    { label: t("myRobots.earned"), value: formatMoney(robot.earned_total, { sign: "always", suffix: "USDT" }), tone },
    { label: t("myRobots.roi"), value: formatPercent(robot.earned_pct, { sign: "always" }), tone },
    { label: t("myRobots.daysRunning"), value: t("myRobots.days", { count: robot.days_running }) },
  ];

  return (
    <Panel as="section" radius="lg" className="flex flex-col gap-3 p-4">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-body font-semibold text-text-primary">{t("myRobots.invested")}</h2>
        <span className="shrink-0 text-h3 font-semibold text-text-primary">
          {formatMoney(robot.invested_total, { suffix: "USDT" })}
        </span>
      </div>

      {pending ? (
        <p className="text-micro font-normal text-status-warning">
          {t("myRobots.capital.pendingTotal", {
            amount: formatMoney(robot.capital_pending, { suffix: "USDT" }),
          })}
        </p>
      ) : null}

      <dl className="flex flex-col">
        {rows.map((row, index) => (
          <div
            key={row.label}
            className={[
              "flex items-center justify-between gap-3 py-2",
              index > 0 ? "border-t border-border-subtle" : "",
            ].join(" ")}
          >
            <dt className="text-caption font-normal text-text-secondary">{row.label}</dt>
            <dd
              className={[
                "text-caption font-semibold",
                row.tone ?? "text-text-primary",
              ].join(" ")}
            >
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
    </Panel>
  );
}
